import type { ComponentProps, CSSProperties } from 'react';
import { useMemo } from 'react';
import clsx from 'clsx';
import { capitalize } from 'lodash';
import { FolderTabSvg } from '@frameio/assets/asset/components/AssetContainer/FolderTabSvg';
import { useFolderColor } from '@frameio/assets/folder/hooks/useFolderColor';
import { useFolderThumbnailItemsFragment } from '@frameio/graphql-client/generated';
import { getThumbMedia } from '@frameio/shared/finder/utils';
import { useIsLabsFeatureEnabled } from '@frameio/shared/hooks';
import { EMPTY_OBJECT } from '@frameio/utils';
import type { DisplayCategories } from '@frameio/vapor';
import { MediaThumbnail, MediaThumbnailGroup } from '@frameio/vapor';
import type { QuickSearchAssetV2Fragment as QuickSearchAssetFragment } from '../gql/generated/QuickSearchAssetV2';
import styles from './FolderThumb.module.css';

const DIMENSIONS = {
  default: {
    thumbOffsetX: 0,
    thumbOffsetY: 22,
    paddingTop: 'var(--spaces-16)',
    strokeWidth: 'var(--spaces-1)',
    columnRatio: '2-1',
    borderRadius: 6,
    padding: 6,
    gap: 4,
  },
  compact: {
    thumbOffsetX: 0,
    thumbOffsetY: 14,
    paddingTop: 'var(--spaces-0)',
    strokeWidth: 'var(--spaces-1)',
    columnRatio: '3-2',
    borderRadius: 2,
    padding: 4,
    gap: 2,
  },
} as const;

type FolderThumbProps = {
  folderId?: string;
  variant?: ComponentProps<typeof FolderTabSvg>['variant'];
  /**
   * Whether the folder thumb needs to be rendered with a contrast foreground color
   */
  folderBackgroundColor?: string;
  /** Width of the folder container in pixels. */
  width: number;
  /** Height of the folder container in pixels. */
  height: number;
};

export const FolderThumb = ({
  folderId,
  variant = 'default',
  folderBackgroundColor,
  width,
  height,
}: FolderThumbProps) => {
  const { data: folderData } = useFolderThumbnailItemsFragment(folderId ?? '');
  const isColoredFolders = useIsLabsFeatureEnabled('folder_colors');
  const { colorToken } = useFolderColor(folderId ?? '');
  const hasCustomFolderColor = isColoredFolders && Boolean(colorToken);

  const folderThumbs = useMemo(
    () => folderData?.thumbnails?.slice(0, 3),
    [folderData]
  );

  // Calculate dimensions: full container minus paddings and offsets
  const mediaWidth = width - DIMENSIONS[variant].thumbOffsetX;
  const mediaHeight = height - DIMENSIONS[variant].thumbOffsetY;
  const hasFolderThumbs = Boolean(folderThumbs?.length);

  /*
   * If a folder background color is provided, use it otherwise use the FolderTabSvg logic.
   * if `fill` is provided, it will override the default logic.
   * A user-provided folder color takes precedence over this contrast override.
   */
  const folderBackgroundColorProps = Boolean(folderBackgroundColor) &&
    !hasCustomFolderColor && {
      fill: folderBackgroundColor,
    };

  return (
    <div
      className={styles.folderContainer}
      style={
        {
          '--folder-padding-top': DIMENSIONS[variant].paddingTop,
          '--folder-stroke-width': DIMENSIONS[variant].strokeWidth,
        } as CSSProperties
      }
    >
      <FolderTabSvg
        className={styles.folderSvg}
        data-folder-tab-svg="true"
        folderId={folderId}
        variant={variant}
        {...folderBackgroundColorProps}
      />
      {hasFolderThumbs && (
        <div
          className={clsx(styles.folderContents, {
            [styles.folderContentsCompact]: variant === 'compact',
          })}
        >
          <MediaThumbnailGroup
            borderRadius={DIMENSIONS[variant].borderRadius}
            columnRatio={DIMENSIONS[variant].columnRatio}
            gap={DIMENSIONS[variant].gap}
            height={mediaHeight}
            padding={DIMENSIONS[variant].padding}
            width={mediaWidth}
          >
            {folderThumbs?.map((thumbnail, index) => {
              const itemType =
                thumbnail && 'filetype' in thumbnail
                  ? thumbnail?.filetype?.toLowerCase()
                  : 'folder';
              const assetTypename = `${capitalize(itemType)}Asset`;
              const key = `${index}-${itemType}-${thumbnail?.id ?? ''}`;

              return (
                <MediaThumbnail
                  key={key}
                  assetTypename={assetTypename}
                  displayCategory={itemType as DisplayCategories}
                  media={
                    getThumbMedia(
                      thumbnail as unknown as QuickSearchAssetFragment
                    ) ?? EMPTY_OBJECT
                  }
                />
              );
            })}
          </MediaThumbnailGroup>
        </div>
      )}
      <FolderTabSvg
        className={styles.folderSvgCover}
        isOverlay={true}
        variant={variant}
      />
    </div>
  );
};
